import React, { useState, useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import { Ellipsis } from "lucide-react";
import AuthContext from "../context/AuthContext";
import {
  cancelFriendRequest,
  createFriendRequest,
  acceptFriendRequest,
  declineFriendRequest,
  fetchFriendRequestStatus,
  toggleFollow,
  unFriend,
} from "../api/UserAPI";
import { useModal } from "./ModalManager";

export default function ProfileCard({ profile, loading }) {
  const { user } = useContext(AuthContext);
  const [isFriend, setIsFriend] = useState(profile?.is_friend ? profile.is_friend : false);
  const [friendReqPending, setFriendReqPending] = useState(false);
  const [friendReq, setFriendReq] = useState(null);
  const { openModal } = useModal();

  useEffect(() => {
    if (!profile?.username) return;
    setIsFriend(Boolean(profile.is_friend));

    const checkFriendRequestStatus = async () => {
      if (profile.is_friend) {
        setFriendReqPending(false);
        return;
      }

      try {
        const requests = await fetchFriendRequestStatus(profile.username);
        const res = requests[0];
        console.log("STATUS::", requests);
        if (res?.status === "pending" || res === "pending") {
          setFriendReqPending(true);
          setFriendReq(res);
        } else {
          setFriendReqPending(false);
          setFriendReq(null);
        }
      } catch (err) {
        console.error("Failed to get status of friend request", err);
        setFriendReqPending(false);
      }
    };

    checkFriendRequestStatus();
  }, [profile?.username, profile?.is_friend]);

  // const handleToggleFollow = async () => {
  //   try {
  //     const res = await toggleFollow(profile.username);
  //     setIsFriend((prev) => !prev);
  //   } catch (err) {
  //     console.error("Failed to toggle follow", err);
  //   }
  // };

  // const handleShowFollowers = () => {
  //   openModal("UserDisplayModal", {
  //     username: profile.username,
  //     mode: "followers",
  //     message: "No followers yet.",
  //   });
  // };

  // const handleShowFollowing = () => {
  //   openModal("UserDisplayModal", {
  //     username: profile.username,
  //     mode: "following",
  //     message: "Not following anyone.",
  //   });
  // };

  const handleUnFriend = async () => {
    try {
      console.log(profile.user_id);
      const res = await unFriend(profile.user_id);
      setIsFriend(false);
      setFriendReqPending(false);
    } catch (err) {
      console.error("Failed to unfriend user", err);
    }
  };

  const handleFriendRequest = async () => {
    try {
      const data = await createFriendRequest(profile.user_id);
      setFriendReq(data);
      setFriendReqPending(true);
    } catch (err) {
      console.error("Failed to create friend request", err);
    }
  };

  const handleCancelFriendRequest = async () => {
    try {
      const statusRes = await fetchFriendRequestStatus(profile.username);
      console.log("STATUS RES", statusRes);
      const friendReqId = statusRes[0]?.id;

      if (!friendReqId) {
        console.error("No pending friend request id found");
        return;
      }

      await cancelFriendRequest(friendReqId, profile.username);
      setFriendReqPending(false);
      setFriendReq(null);
    } catch (err) {
      console.error("Failed to cancel friend request", err);
    }
  };

  const handleAcceptFriendReq = async () => {
    try {
      await acceptFriendRequest(friendReq.id, profile.username);
      setIsFriend(true);
      setFriendReqPending(false);
      setFriendReq(null);
    } catch (err) {
      console.error("Failed to accept friend request", err);
    }
  };

  const handleDeclineFriendReq = async () => {
    try {
      await declineFriendRequest(friendReq.id, profile.username);
      setIsFriend(false);
      setFriendReqPending(false);
      setFriendReq(null);
    } catch (err) {
      console.error("Failed to decline friend request", err);
    }
  };

  const getFriendButtonConfig = () => {
    if (isFriend) {
      return {
        label: "Unfriend",
        onClick: handleUnFriend,
        className: "bg-gray-600 hover:bg-red-500",
      };
    }

    if (friendReqPending && friendReq?.from_user === user.id) {
      return {
        label: "Cancel Request",
        onClick: handleCancelFriendRequest,
        className: "bg-gray-500 hover:bg-red-500",
      };
    }

    return {
      label: "Add Friend",
      onClick: handleFriendRequest,
      className: "bg-blue-600 hover:bg-blue-700",
    };
  };

  const isOutgoingFriendReq = friendReqPending && friendReq?.from_user === user.id;

  const isIncomingFriendReq = friendReqPending && friendReq?.to_user === user.id;

  const friendButton = getFriendButtonConfig();

  if ((!profile || profile.length == 0 || !user) && !loading) {
    return <p className="text-center p-4">User Not Found...</p>;
  }

  const isSelf = profile.username === user?.username;
  const avatar = profile.profile_pic_url || `${process.env.PUBLIC_URL}/logo512.png`;

  if (loading) {
    return (
      <section className="mx-auto mt-8 max-w-2xl rounded-2xl border bg-white/50 p-5 shadow-sm mb-3">
        {/* Header */}
        <header className="flex items-start gap-5">
          <img
            src={`${process.env.PUBLIC_URL}/logo512.png`}
            alt={`${profile.username} avatar`}
            className="size-24 rounded-full border object-cover"
          />

          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-semibold leading-none">loading...</h1>
              <div className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-sm hover:bg-gray-50">
                <Ellipsis className="h-4 w-4" />
              </div>
            </div>

            {(profile.first_name || profile.last_name) && (
              <p className="mt-1 text-sm text-gray-600">...</p>
            )}
          </div>
        </header>

        {/* Stats */}
        <dl className="mt-5 grid grid-cols-2 gap-3 text-sm sm:grid-cols-2">
          <Stat label="Likes" value={0} />
          <Stat label="Friends" value={0} />
          {/* <Stat label="Followers" value={0} />
          <Stat label="Following" value={0} /> */}
        </dl>

        {/* Bio */}
        <div className="mt-4">
          <h2 className="mb-1 text-sm font-medium text-gray-700">Bio</h2>
          <p className="rounded-lg border bg-gray-50/60 p-3 text-sm leading-relaxed">
            {profile.bio?.trim() || "No bio yet"}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto mt-8 max-w-2xl rounded-2xl border bg-white/50 p-5 shadow-sm mb-3">
      {/* Header */}
      <header className="flex items-start gap-5">
        <img
          src={avatar}
          alt={`${profile.username} avatar`}
          className="size-24 rounded-full border object-cover"
        />

        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold leading-none">{profile.username}</h1>

            {isSelf ? (
              <Link
                to={`/profiles/settings/`}
                className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-sm hover:bg-gray-50"
                aria-label="Edit profile"
                title="Edit profile"
              >
                <Ellipsis className="h-4 w-4" />
              </Link>
            ) : isIncomingFriendReq ? (
              <div className="inline-flex items-center gap-2">
                <button
                  onClick={handleAcceptFriendReq}
                  className="inline-flex items-center rounded-lg bg-blue-600 hover:bg-blue-700 px-3 py-1.5 text-sm font-medium text-white"
                >
                  Accept
                </button>

                <button
                  onClick={handleDeclineFriendReq}
                  className="inline-flex items-center rounded-lg bg-gray-500 hover:bg-red-500 px-3 py-1.5 text-sm font-medium text-white"
                >
                  Decline
                </button>
              </div>
            ) : (
              <button
                onClick={friendButton.onClick}
                className={`inline-flex items-center rounded-lg ${friendButton.className} px-3 py-1.5 text-sm font-medium text-white`}
              >
                {friendButton.label}
              </button>
            )}
          </div>

          {(profile.first_name || profile.last_name) && (
            <p className="mt-1 text-sm text-gray-600">
              {profile.first_name} {profile.last_name}
            </p>
          )}
        </div>
      </header>

      {/* Stats */}
      <dl className="mt-5 grid grid-cols-2 gap-3 text-sm sm:grid-cols-2">
        <Stat label="Hearts" value={profile.likes_received} />
        <Stat label="Friends" value={profile.friends_count} />
        {/* <Stat
          onClick={() => handleShowFollowers(profile)}
          label="Followers"
          value={profile.followers_count}
        /> */}
        {/* <Stat
          onClick={() => handleShowFollowing()}
          label="Following"
          value={profile.following_count}
        /> */}
      </dl>

      {/* Bio */}
      <div className="mt-4">
        <h2 className="mb-1 text-sm font-medium text-gray-700">Bio</h2>
        <p className="rounded-lg border bg-gray-50/60 p-3 text-sm leading-relaxed">
          {profile.bio?.trim() || "No bio yet"}
        </p>
      </div>
    </section>
  );
}

function Stat({ label, value, onClick }) {
  return (
    <div onClick={onClick} className="rounded-lg border p-3 text-center">
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 text-base font-semibold tabular-nums">{value ?? 0}</dd>
    </div>
  );
}
